import React, { useLayoutEffect, useState, lazy, Suspense } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { neighborhoods } from '../constants/neighborhoods';
import FadeInSection from './FadeInSection';
import ContactModal from './ContactModal';
import { Helmet } from 'react-helmet-async';
import '../styles/NeighborhoodDetail.scss';
import { motion, useScroll, useTransform } from 'framer-motion';

// Pulls in leaflet + @turf. Below the fold, and only some neighborhoods have sub-areas.
const SubNeighborhoodMap = lazy(() => import('./SubNeighborhoodMap'));

const slideUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const heroStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11, delayChildren: 0.15 } },
};

function scoreClass(n) {
  if (n >= 90) return 'score-excellent';
  if (n >= 70) return 'score-good';
  if (n >= 50) return 'score-fair';
  return 'score-low';
}

function walkLabel(n) {
  if (n >= 90) return "Walker's Paradise";
  if (n >= 70) return 'Very Walkable';
  if (n >= 50) return 'Walkable';
  return 'Car-Dependent';
}

function bikeLabel(n) {
  if (n >= 90) return "Biker's Paradise";
  if (n >= 70) return 'Very Bikeable';
  if (n >= 50) return 'Bikeable';
  return 'Minimal Infrastructure';
}

function transitLabel(n) {
  if (n >= 90) return "Rider's Paradise";
  if (n >= 70) return 'Excellent Transit';
  if (n >= 50) return 'Good Transit';
  if (n >= 25) return 'Some Transit';
  return 'Minimal Transit';
}


function NeighborhoodDetail() {
  const { slug } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const n = neighborhoods.find(nb => nb.slug === slug);
  const { scrollY } = useScroll();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const heroParallax = useTransform(scrollY, [0, 600], [0, reduced ? 0 : 160]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!n) {
    return (
      <div className="neighborhood-not-found">
        <h1>Neighborhood not found</h1>
        <NavLink to="/neighborhoods">← Back to Neighborhoods</NavLink>
      </div>
    );
  }

  const sameRegion = neighborhoods.filter(nb => nb.slug !== slug && nb.region === n.region);
  const otherRegion = neighborhoods.filter(nb => nb.slug !== slug && nb.region !== n.region);
  const related = [...sameRegion, ...otherRegion].slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{n.name} Neighborhood Guide — Homes, Market & Local Life | Lily Lai Real Estate</title>
        <meta name="description" content={`${n.name} real estate guide: median home price ${n.market.medianPrice}, walk score ${n.scores.walk}. ${n.tagline}. Local restaurants, schools, and market data from Lily Lai.`} />
        <link rel="canonical" href={`https://www.lilylaisf.com/neighborhoods/${n.slug}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${n.name} Neighborhood Guide | Lily Lai Real Estate`} />
        <meta property="og:description" content={`${n.tagline}. Median price ${n.market.medianPrice} · Walk Score ${n.scores.walk}. Explore local restaurants, schools, and market trends.`} />
        <meta property="og:url" content={`https://www.lilylaisf.com/neighborhoods/${n.slug}`} />
        {n.image && <meta property="og:image" content={n.image} />}
        {n.image && <meta property="og:image:alt" content={`${n.name} neighborhood — ${n.region === 'sf' ? 'San Francisco' : 'Bay Area Peninsula'}`} />}
        <meta property="og:site_name" content="Lily Lai Real Estate" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${n.name} Neighborhood Guide | Lily Lai`} />
        <meta name="twitter:description" content={`${n.tagline}. Median price ${n.market.medianPrice} · Walk Score ${n.scores.walk}.`} />
        {n.image && <meta name="twitter:image" content={n.image} />}
        {n.image && <meta name="twitter:image:alt" content={`${n.name} neighborhood — ${n.region === 'sf' ? 'San Francisco' : 'Bay Area Peninsula'}`} />}

        {/* JSON-LD: single @graph for clean schema graph */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.lilylaisf.com/" },
                { "@type": "ListItem", "position": 2, "name": "Neighborhoods", "item": "https://www.lilylaisf.com/neighborhoods" },
                { "@type": "ListItem", "position": 3, "name": n.name, "item": `https://www.lilylaisf.com/neighborhoods/${n.slug}` },
              ]
            },
            {
              "@type": "Place",
              "name": n.name,
              "description": n.description,
              "url": `https://www.lilylaisf.com/neighborhoods/${n.slug}`,
              ...(n.image ? { "image": n.image } : {}),
              ...(n.mapCenter ? {
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": n.mapCenter[0],
                  "longitude": n.mapCenter[1]
                }
              } : {}),
              "address": {
                "@type": "PostalAddress",
                "addressLocality": n.region === 'sf' ? 'San Francisco' : n.name,
                "addressRegion": "CA",
                "addressCountry": "US"
              },
              "containedInPlace": {
                "@type": n.region === 'sf' ? "City" : "AdministrativeArea",
                "name": n.region === 'sf' ? 'San Francisco' : 'San Mateo County',
                "containedInPlace": {
                  "@type": "State",
                  "name": "California",
                  "containedInPlace": { "@type": "Country", "name": "United States" }
                }
              },
              ...(n.market ? {
                "additionalProperty": [
                  { "@type": "PropertyValue", "name": "Median Home Price", "value": n.market.medianPrice },
                  { "@type": "PropertyValue", "name": "Price Per Square Foot", "value": n.market.pricePerSqft },
                  { "@type": "PropertyValue", "name": "Average Days on Market", "value": String(n.market.avgDaysOnMarket) },
                  { "@type": "PropertyValue", "name": "Market Data Updated", "value": n.market.updated }
                ]
              } : {})
            },
            {
              "@type": "RealEstateAgent",
              "name": "Lily Lai Real Estate",
              "url": "https://www.lilylaisf.com",
              "telephone": "+14157282623",
              "email": "lily@lilylaisf.com",
              "areaServed": [
                { "@type": "City", "name": n.region === 'sf' ? 'San Francisco' : n.name, "addressRegion": "CA" }
              ],
              "knowsAbout": [
                "San Francisco real estate", "Bay Area home buying", "Peninsula real estate",
                `${n.name} homes for sale`, `${n.name} neighborhood guide`
              ],
              "description": `Lily Lai is a Bay Area real estate agent specializing in ${n.name} and surrounding neighborhoods.`
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": `What is the median home price in ${n.name}?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `The median home price in ${n.name} is ${n.market.medianPrice} as of ${n.market.updated}, with a price per square foot of ${n.market.pricePerSqft} and homes averaging ${n.market.avgDaysOnMarket} days on market.`
                  }
                },
                {
                  "@type": "Question",
                  "name": `Is ${n.name} a good place to buy a home?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `${n.name} is a ${n.vibe.join(', ').toLowerCase()} neighborhood in the Bay Area. ${n.tagline}. With a median home price of ${n.market.medianPrice} and homes averaging ${n.market.avgDaysOnMarket} days on market, it is ${n.market.avgDaysOnMarket <= 14 ? 'a highly competitive' : 'an active'} market. Contact Lily Lai for personalized guidance.`
                  }
                },
                {
                  "@type": "Question",
                  "name": `How competitive is the ${n.name} real estate market?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `The ${n.name} real estate market is ${n.market.avgDaysOnMarket <= 10 ? 'very competitive' : n.market.avgDaysOnMarket <= 18 ? 'competitive' : 'moderately active'}, with homes averaging ${n.market.avgDaysOnMarket} days on market at ${n.market.medianPrice} median and ${n.market.pricePerSqft}/sqft. Multiple offers are common on desirable listings.`
                  }
                },
                {
                  "@type": "Question",
                  "name": `Is ${n.name} walkable?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `${n.name} has a Walk Score of ${n.scores.walk} (${walkLabel(n.scores.walk)}), a Bike Score of ${n.scores.bike} (${bikeLabel(n.scores.bike)}), and a Transit Score of ${n.scores.transit} (${transitLabel(n.scores.transit)}).`
                  }
                },
                {
                  "@type": "Question",
                  "name": `What is ${n.name} like to live in?`,
                  "acceptedAnswer": { "@type": "Answer", "text": n.description }
                },
                {
                  "@type": "Question",
                  "name": `What are the best restaurants in ${n.name}?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `Top restaurants in ${n.name} include ${n.restaurants.map(r => `${r.name} (${r.type})`).join(', ')}.`
                  }
                },
                {
                  "@type": "Question",
                  "name": `What schools are in ${n.name}?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `Schools in ${n.name} include ${n.schools.map(s => `${s.name} (${s.type}, grades ${s.grades})`).join(', ')}.`
                  }
                },
                {
                  "@type": "Question",
                  "name": `What things are there to do in ${n.name}?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `Popular things to do in ${n.name} include ${n.thingsToDo.map(t => `${t.name} (${t.type})`).join(', ')}.`
                  }
                },
                ...(n.subNeighborhoods?.length > 0 ? [
                  {
                    "@type": "Question",
                    "name": `What are the neighborhoods within ${n.name}?`,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": `${n.name} includes several distinct neighborhoods: ${n.subNeighborhoods.map(s => `${s.name} (${s.priceRange}) — ${s.vibe.join(', ').toLowerCase()}`).join('; ')}.`
                    }
                  },
                  {
                    "@type": "Question",
                    "name": `What is the most affordable neighborhood in ${n.name}?`,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": `The most accessible price points in ${n.name} are in ${n.subNeighborhoods[n.subNeighborhoods.length - 1].name} (${n.subNeighborhoods[n.subNeighborhoods.length - 1].priceRange}) and ${n.subNeighborhoods[n.subNeighborhoods.length - 2]?.name ?? n.subNeighborhoods[0].name} (${n.subNeighborhoods[n.subNeighborhoods.length - 2]?.priceRange ?? n.subNeighborhoods[0].priceRange}). Contact Lily Lai for current availability.`
                    }
                  }
                ] : [])
              ]
            }
          ]
        })}</script>
      </Helmet>
      <div className="neighborhood-detail">

        {/* Hero */}
        <div className={`nd-hero ${!n.image ? 'nd-hero-placeholder' : ''}`}>
          {n.image && (
            <motion.div
              className="nd-hero-bg"
              style={{ backgroundImage: `url(${n.image})`, y: heroParallax }}
            />
          )}
          <div className="nd-hero-overlay" />
          <motion.div
            className="nd-hero-content"
            variants={heroStagger}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={slideUp}>
              <NavLink to="/neighborhoods" className="nd-back">← All Neighborhoods</NavLink>
            </motion.div>
            <motion.p className="nd-eyebrow" variants={slideUp}>
              {n.region === 'sf' ? 'San Francisco' : 'Peninsula'}
            </motion.p>
            <motion.h1 variants={slideUp}>{n.name}</motion.h1>
            <motion.p className="nd-tagline" variants={slideUp}>{n.tagline}</motion.p>
          </motion.div>
        </div>

        <div className="nd-body">

          {/* Vibe tags */}
          <FadeInSection>
            <div className="nd-vibe">
              {n.vibe.map(v => <span key={v} className="nd-vibe-tag">{v}</span>)}
            </div>
          </FadeInSection>

          {/* Sub-neighborhoods map */}
          {n.subNeighborhoods && n.subNeighborhoods.length > 0 && n.mapCenter && (
            <FadeInSection>
              <div className="nd-subnbhds">
                <p className="nd-section-eyebrow">Neighborhoods Within {n.name}</p>
                <Suspense fallback={<div style={{ height: '520px' }} />}>
                  <SubNeighborhoodMap
                    subNeighborhoods={n.subNeighborhoods}
                    mapCenter={n.mapCenter}
                    mapZoom={n.mapZoom}
                    cityName={n.name}
                  />
                </Suspense>
              </div>
            </FadeInSection>
          )}

          {/* About + Market */}
          <div className="nd-main">
            <FadeInSection>
              <div className="nd-about">
                <p className="nd-section-eyebrow">About</p>
                <h2>{n.name}</h2>
                <p className="nd-description">{n.description}</p>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="nd-market">
                <p className="nd-section-eyebrow">Market Data</p>
                <p className="nd-market-updated">Data as of <time>{n.market.updated}</time></p>
                <div className="nd-market-stats">
                  <div className="nd-market-stat">
                    <span className="nd-market-value">{n.market.medianPrice}</span>
                    <span className="nd-market-label">Median Sale Price</span>
                  </div>
                  <div className="nd-market-stat">
                    <span className="nd-market-value">{n.market.pricePerSqft}</span>
                    <span className="nd-market-label">Price Per Sq Ft</span>
                  </div>
                  <div className="nd-market-stat">
                    <span className="nd-market-value">{n.market.avgDaysOnMarket}</span>
                    <span className="nd-market-label">Avg Days on Market</span>
                  </div>
                </div>
                <p className="nd-market-disclaimer">
                  Market data is approximate and based on recent MLS activity. Contact Lily for a current, detailed market analysis.
                </p>
              </div>
            </FadeInSection>
          </div>

          {/* Livability Scores */}
          <FadeInSection>
            <div className="nd-scores">
              <p className="nd-section-eyebrow">Livability</p>
              <div className="nd-scores-grid">
                {[
                  { value: n.scores.walk, label: 'Walk Score', desc: walkLabel(n.scores.walk) },
                  { value: n.scores.bike, label: 'Bike Score', desc: bikeLabel(n.scores.bike) },
                  { value: n.scores.transit, label: 'Transit Score', desc: transitLabel(n.scores.transit) },
                ].map((score, i) => (
                  <div key={score.label} className="nd-score-item">
                    <motion.div
                      className={`nd-score-badge ${scoreClass(score.value)}`}
                      aria-label={`${score.label}: ${score.value} — ${score.desc}`}
                      initial={{ opacity: reduced ? 1 : 0, scale: reduced ? 1 : 0.4 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ type: 'spring', stiffness: 260, damping: 18, delay: reduced ? 0 : i * 0.1 }}
                    >
                      <span aria-hidden="true">{score.value}</span>
                    </motion.div>
                    <span className="nd-score-label">{score.label}</span>
                    <span className="nd-score-desc">{score.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeInSection>

          {/* Demographics */}
          <FadeInSection>
            <div className="nd-demo">
              <p className="nd-section-eyebrow">Neighborhood Snapshot</p>
              <div className="nd-demo-grid">
                <div className="nd-demo-stat">
                  <span className="nd-demo-value">{n.demographics.population}</span>
                  <span className="nd-demo-label">Population</span>
                </div>
                <div className="nd-demo-stat">
                  <span className="nd-demo-value">{n.demographics.medianAge}</span>
                  <span className="nd-demo-label">Median Age</span>
                </div>
                <div className="nd-demo-stat">
                  <span className="nd-demo-value">{n.demographics.medianIncome}</span>
                  <span className="nd-demo-label">Median Income</span>
                </div>
                <div className="nd-demo-stat">
                  <span className="nd-demo-value">{n.demographics.medianRent}</span>
                  <span className="nd-demo-label">Median Rent</span>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Restaurants + Things To Do */}
          <div className="nd-local">
            <FadeInSection>
              <div className="nd-local-section">
                <p className="nd-section-eyebrow">Restaurants</p>
                <h2>Where to Eat</h2>
                <div className="nd-local-grid">
                  {n.restaurants.map(r => (
                    <div key={r.name} className="nd-local-card">
                      <div className="nd-local-card-header">
                        <span className="nd-local-name">{r.name}</span>
                        <span className="nd-local-type">{r.type}</span>
                      </div>
                      <p className="nd-local-note">{r.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="nd-local-section">
                <p className="nd-section-eyebrow">Things To Do</p>
                <h2>Explore the Area</h2>
                <div className="nd-local-grid">
                  {n.thingsToDo.map(t => (
                    <div key={t.name} className="nd-local-card">
                      <div className="nd-local-card-header">
                        <span className="nd-local-name">{t.name}</span>
                        <span className="nd-local-type">{t.type}</span>
                      </div>
                      <p className="nd-local-note">{t.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInSection>
          </div>

          {/* Schools */}
          <FadeInSection>
            <div className="nd-schools">
              <p className="nd-section-eyebrow">Education</p>
              <h2>Schools Nearby</h2>
              <div className="nd-local-grid">
                {n.schools.map(s => (
                  <div key={s.name} className="nd-local-card">
                    <div className="nd-local-card-header">
                      <span className="nd-local-name">{s.name}</span>
                      <span className="nd-local-type">{s.type}</span>
                    </div>
                    <div className="nd-school-meta">
                      <span className="nd-local-note">Grades {s.grades}</span>
                      {s.rating && (
                        <span className={`nd-school-rating nd-school-rating-${s.rating >= 8 ? 'high' : s.rating >= 6 ? 'mid' : 'low'}`}>
                          {s.rating}/10
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeInSection>

          {/* Explore More */}
          {related.length > 0 && (
            <FadeInSection>
              <div className="nd-explore">
                <p className="nd-section-eyebrow">Explore More</p>
                <h2>More Neighborhoods</h2>
                <div className="nd-explore-grid">
                  {related.map(nb => (
                    <NavLink
                      to={`/neighborhoods/${nb.slug}`}
                      className="neighborhood-card"
                      key={nb.slug}
                      onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'neighborhood_related_click', { neighborhood_name: nb.name }); }}
                    >
                      <div className="neighborhood-card-image">
                        {nb.image
                          ? <img src={nb.image} alt={nb.name} loading="lazy" />
                          : <div className="neighborhood-card-placeholder" />
                        }
                        <div className="neighborhood-card-overlay">
                          <span className="neighborhood-card-region">
                            {nb.region === 'sf' ? 'San Francisco' : 'Peninsula'}
                          </span>
                          <div className="neighborhood-card-info">
                            <h3 className="neighborhood-card-name">{nb.name}</h3>
                            <p className="neighborhood-card-tagline">{nb.tagline}</p>
                          </div>
                        </div>
                      </div>
                    </NavLink>
                  ))}
                </div>
                <NavLink to="/neighborhoods" className="nd-explore-all link-underline">View All Neighborhoods →</NavLink>
              </div>
            </FadeInSection>
          )}

          {/* CTA */}
          <div className="nd-cta" style={n.image ? { backgroundImage: `url(${n.image})` } : {}}>
            <div className="nd-cta-overlay" />
            <motion.div
              className="nd-cta-content"
              initial={{ opacity: reduced ? 1 : 0, y: reduced ? 0 : 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: reduced ? 0 : 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="nd-cta-eyebrow">Get In Touch</p>
              <h2 className="nd-cta-heading">Interested in {n.name}?</h2>
              <p className="nd-cta-body">Whether you're buying or selling, I'd love to share my firsthand knowledge of this neighborhood and help you find the right opportunity.</p>
              <ContactModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                trigger={
                  <button
                    className="nd-cta-btn"
                    onClick={() => {
                      setIsOpen(true);
                      if (typeof window.gtag === 'function') window.gtag('event', 'neighborhood_cta_click', { neighborhood_name: n.name });
                    }}
                  >
                    Schedule a Consultation
                  </button>
                }
              />
            </motion.div>
          </div>

        </div>
      </div>
    </>
  );
}

export default NeighborhoodDetail;
